import { createActions, createReducer } from "reduxsauce";
import { cloneDeep, get, set, forEach } from "@utils/lodash";

export { default as thunks } from "./thunks";

/* ------------- Initial State ------------- */
export const INITIAL_STATE = {
	data: {
		counts: {},
		listData: {},
		checks: [],
		clients: [],
		projects: [],
		locations: [],
		details: {},
	},
	invoiceSummary: {},
	receipt: {},
	loading: {
		counts: false,
		listData: false,
		clients: false,
		projects: false,
		locations: false,
		invoiceSummary: false,
		receipt: false,
		paymentModal: false,
		details: false,
		history: false,
		disputeForm: false,
	},
	filters: {
		displayCount: 25,
		pageNumber: 1,
		sortBy: "StartDateTime",
		asc: true,
		locationId: null,
		clientId: null,
		projectId: null,
		officerId: null,
		jobStartDate: null,
		jobEndDate: null,
		invoiceStartDate: null,
		invoiceEndDate: null,
		sameDayPaid: null,
		prePay: null,
	},
	modals: {
		disputeForm: false,
		paymentSummaryModal: false,
		invoiceForm: false,
	},
	selectAll: {
		review: false,
		payment: false,
	},
	history: {
		data: [],
		displayCount: 25,
		hasNext: false,
		pagingToken: null,
	},
	disputeConfirmation: false,
	disputeReasons: [],
	totalCountForCheckbox: 0,
	activeDisputeId: "",
	activeInvoiceIds: [],
	activeTransactionId: "",
};

/* ------------- Types and Action Creators ------------- */
export const { Types, Creators } = createActions(
	{
		receiveData: ["collection"],
		setLoading: ["property", "value"],
		updateFilters: ["collection"],
		updateModalState: ["modal", "value"],
		updateStoreProperties: ["collection"],
		updateSelection: ["index", "viewType"],
		toggleSelectAll: ["viewType", "newValue"],
		resetData: ["collection"],
		resetFilters: null,
		resetHistory: null,
		clearSelection: null,
		loadBulkPaymentInvoices: ["invoices"],
		updatePayoutHistory: ["data"],
		loadMorePayouts: ["data"],
	},
	{ prefix: "@payments/" },
);

/* ------------- Reducers ------------- */

export const reducer = createReducer(INITIAL_STATE, {
	[Types.RECEIVE_DATA]: (state, { collection }) => {
		const newState = cloneDeep(state);

		forEach(collection, ({ property, value }) => {
			set(newState, ["data", property], value);
		});

		return newState;
	},

	[Types.UPDATE_STORE_PROPERTIES]: (state, { collection }) => {
		const newState = cloneDeep(state);

		forEach(collection, (value, property) => set(newState, property, value));

		return newState;
	},

	[Types.SET_LOADING]: (state, { property, value }) => {
		const newState = cloneDeep(state);

		set(newState, ["loading", property], value);

		return newState;
	},

	[Types.UPDATE_MODAL_STATE]: (state, { modal, value }) => {
		const newState = cloneDeep(state);
		set(newState, ["modals", modal], value);

		return newState;
	},

	[Types.UPDATE_FILTERS]: (state, { collection }) => {
		const newState = cloneDeep(state);

		forEach(collection, ({ property, value }) => {
			set(newState, ["filters", property], value);
		});

		return newState;
	},

	[Types.UPDATE_SELECTION]: (state, { index, viewType }) => {
		const newState = cloneDeep(state);
		const checks = get(newState, ["data", "checks"], []);
		const count = get(newState, ["data", "listData", "Count"], 0);

		if (!checks.includes(index)) {
			checks.push(index);
		} else {
			const indexOfUncheck = checks.indexOf(index);
			checks.splice(indexOfUncheck, 1);
		}
		set(newState, ["data", "checks"], checks);
		if (count === checks.length) {
			set(newState, ["selectAll", `${viewType}`], true);
		} else {
			set(newState, ["selectAll", `${viewType}`], false);
		}

		set(newState, ["data", "checks"], checks);

		return newState;
	},

	[Types.CLEAR_SELECTION]: state => ({
		...state,
		data: {
			...state.data,
			checks: INITIAL_STATE.data.checks,
		},
		selectAll: INITIAL_STATE.selectAll,
	}),

	[Types.TOGGLE_SELECT_ALL]: (state, { viewType, newValue }) => {
		const newState = cloneDeep(state);
		let checks;

		if (newValue) {
			const count = get(newState, ["data", "listData", "Count"], 0);
			const displayCount = get(newState, ["filters", "displayCount"], 25);
			checks = [...Array(count > displayCount ? displayCount : count).keys()];
		} else {
			checks = [];
		}

		set(newState, ["selectAll", `${viewType}`], newValue);
		set(newState, ["data", "checks"], checks);

		return newState;
	},

	[Types.RESET_FILTERS]: state => {
		const newState = cloneDeep(state);

		set(newState, ["data", "checks"], INITIAL_STATE.data.checks);
		set(newState, ["selectAll"], INITIAL_STATE.selectAll);
		set(newState, ["filters"], INITIAL_STATE.filters);

		return newState;
	},

	[Types.RESET_DATA]: (state, { collection = null }) => {
		if (collection) {
			const newState = cloneDeep(state);
			forEach(collection, property => {
				set(newState, property, get(INITIAL_STATE, property));
			});

			return newState;
		}

		return INITIAL_STATE;
	},

	[Types.LOAD_BULK_PAYMENT_INVOICES]: state => {
		const newState = cloneDeep(state);
		const invoices = get(newState, ["invoiceSummary", "TransactionSummaryItems"], []);
		const invoiceIds = [];

		invoices.forEach(invoice => {
			invoiceIds.push(invoice.InvoiceId);
		});

		set(newState, ["activeInvoiceIds"], invoiceIds);

		return newState;
	},

	[Types.UPDATE_PAYOUT_HISTORY]: (state, { data }) => {
		const { HasNext, PagingToken, ItemList } = data;

		const newState = cloneDeep(state);

		set(newState, ["history", "hasNext"], HasNext);
		set(newState, ["history", "pagingToken"], PagingToken);
		set(newState, ["history", "data"], ItemList);

		return newState;
	},

	[Types.LOAD_MORE_PAYOUTS]: (state, { data }) => {
		const { HasNext, PagingToken, ItemList } = data;
		const newState = cloneDeep(state);
		const currentRows = get(state, ["history", "data"]);
		const newItemList = currentRows.concat(ItemList);

		set(newState, ["history", "hasNext"], HasNext);
		set(newState, ["history", "pagingToken"], PagingToken);
		set(newState, ["history", "data"], newItemList);

		return newState;
	},

	[Types.RESET_HISTORY]: state => ({
		...state,
		history: INITIAL_STATE.history,
	}),
});

export default Creators;
